/**
 * Ledger Timeline Component - Displays ledger entries in a timeline view
 */

import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import {
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  try {
    return format(new Date(dateString), "MMM dd, yyyy HH:mm");
  } catch {
    return "Invalid date";
  }
};

const getEntryTypeLabel = (entryType) => {
  const labels = {
    CUSTOMER_CHARGE: "Charge",
    CUSTOMER_PAYMENT: "Payment",
    CUSTOMER_REFUND: "Refund",
    PROVIDER_PAYOUT: "Payout",
    PROVIDER_RETENTION_HOLD: "Retention Hold",
    RETENTION_RELEASE: "Retention Release",
    JOB_ADJUSTMENT: "Adjustment",
    PLATFORM_FEE: "Platform Fee",
    PRODUCT_SALE: "Product Sale",
  };
  return labels[entryType] || entryType;
};

const getDirectionColor = (direction) => {
  return direction === "DEBIT" ? "error" : "success";
};

const LedgerTimeline = ({ entries, loading, error, title = "Financial Timeline" }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          No ledger entries found
        </Typography>
      </Box>
    );
  }

  // Sort entries chronologically (oldest first) for legible timeline
  const sortedEntries = useMemo(() => {
    const getDisplayTime = (entry) => {
      return new Date(entry.occurredAt || entry.recordedAt || entry.createdAt).getTime();
    };
    return [...entries].sort((a, b) => getDisplayTime(a) - getDisplayTime(b));
  }, [entries]);

  return (
    <Box>
      {title && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.map((entry) => (
              <TableRow key={entry.id || entry._id}>
                <TableCell>{formatDate(entry.occurredAt || entry.recordedAt || entry.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={getEntryTypeLabel(entry.entryType)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {entry.direction === "DEBIT" ? (
                    <ArrowDownward color="error" fontSize="small" />
                  ) : (
                    <ArrowUpward color="success" fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ ml: 0.5 }}
                    color={getDirectionColor(entry.direction) + ".main"}
                  >
                    {entry.direction}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={getDirectionColor(entry.direction) + ".main"}
                  >
                    {formatCurrency(entry.amount)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {entry.metadata?.reference || entry.reference?.sourceId || "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LedgerTimeline;
